import { backendApi } from "@/services/api";
import { Procedure } from "@/services/backend/procedures/procedure/procedure";
import { ProcedureType, SampleType } from "@/services/backend/types";
import { Filter } from "@/shared/lib/filter/filter";

const procedureApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    startProcedure: build.mutation<
      StartProcedureResponse,
      StartProcedureRequest
    >({
      query: (request) => ({
        url: `/inspections/procedures`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [
        "Procedures",
        "SampleSet",
        "SampleSetDetail",
        "Process",
      ],
    }),
    putProcedure: build.mutation<Procedure, PutRequest>({
      query: (request) => ({
        url: `/inspections/procedures`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["Procedures"],
    }),
    listProcedures: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/inspections/procedures`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: ["Procedures"],
    }),
    showProcedureBySampleSet: build.query<
      Procedure,
      ShowProcedureBySampleSetRequest
    >({
      query: (request) => ({
        url: `/inspections/procedures/samplesets/${request.sampleSetId}`,
        method: "GET",
      }),
      providesTags: ["Procedures"],
    }),
    addMeasurement: build.mutation<
      AddMeasurementResponse,
      AddMeasurementRequest
    >({
      query: (request) => ({
        url: `/inspections/procedures/incubations/measurements`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: ["Procedures"],
    }),
    addMeasurements: build.mutation<
      AddMeasurementsResponse,
      AddMeasurementsRequest
    >({
      query: (request) => ({
        url: `/inspections/procedures/incubations/measurements`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["Procedures"],
    }),
  }),
});

export const {
  usePutProcedureMutation,
  useStartProcedureMutation,
  useListProceduresQuery,
  useAddMeasurementMutation,
  useAddMeasurementsMutation,
  useShowProcedureBySampleSetQuery,
} = procedureApi;

interface StartProcedureRequest {
  processId: string;
}

export interface PutRequest {
  id: string;
  sampleSetId: string;
  processId: string;
  description?: string;
  comment?: string;
  holeCount?: number;
}

interface StartProcedureResponse {}

interface ListRequest {
  ownerId?: Filter<string>;
  ownerType?: Filter<string>;
  procedureType?: Filter<ProcedureType>;
  sampleType?: Filter<SampleType>;
  limit: number;
  offset: number;
}

interface List {
  count: number;
  items: Procedure[];
}

export interface AddMeasurementRequest {
  id: string;
  incubationId: string;
  procedureId: string;
  measurementTime: string;
  value?: number;
  employeeId: string;
  exceedsThreshold: boolean;
}

interface AddMeasurementResponse {}

export interface AddMeasurementsRequest {
  processId: string;
  measurements: AddMeasurementRequest[];
}

interface AddMeasurementsResponse {}

interface ShowProcedureBySampleSetRequest {
  sampleSetId: string;
}
